import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { addWeeks, endOfISOWeek, isDate, startOfISOWeek } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { Range, RangeKeyDict } from 'react-date-range';
import { DateRange } from 'react-date-range';
import { format } from '../../../utils/formatting';
import Tooltip from '../../../utils/tooltip';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

function addWeeksFp(amount: number) {
    return (date: Date) => addWeeks(date, amount);
}

export function applyModifierForTimezone(
    date: Date,
    timezone: string,
    modifier: (date: Date) => Date
): Date {
    const zonedDate = utcToZonedTime(date, timezone);

    // Step 2: Get the start of the day in the specified timezone.
    const zonedWithModifier = modifier(zonedDate);

    // Step 3: Convert the zoned start of day to a UTC-equivalent timestamp.
    return zonedTimeToUtc(zonedWithModifier, timezone);
}

export interface WeekRange {
    startDate: Date;
    endDate: Date;
    key: string;
}

function getThisWeekForTimezone(timezone: string): WeekRange {
    return Object.freeze({
        startDate: applyModifierForTimezone(new Date(), timezone, startOfISOWeek),
        endDate: applyModifierForTimezone(new Date(), timezone, endOfISOWeek),
        key: 'selection',
    });
}

export function useThisWeekForTimezone(
    timezone: string
): [WeekRange, Dispatch<SetStateAction<WeekRange>>] {
    return useState(getThisWeekForTimezone(timezone));
}

interface DateButtonProps {
    icon: IconDefinition;
    onClick: () => void;
}

function DateButton({ icon, onClick }: DateButtonProps): JSX.Element {
    return (
        <button onClick={onClick}>
            <FontAwesomeIcon icon={icon} className="text-dark-gray" size="lg" />
        </button>
    );
}

function getWeekRangeForDate(date: Date): WeekRange {
    return { key: 'selection', startDate: startOfISOWeek(date), endDate: endOfISOWeek(date) };
}

function applyModifierToRange(range: WeekRange, modifier: (date: Date) => Date): WeekRange {
    return {
        ...range,
        startDate: modifier(range.startDate),
        endDate: modifier(range.endDate),
    };
}

export interface WeekPickerProps {
    range: WeekRange;
    onChange: Dispatch<SetStateAction<WeekRange>>;
    timezone: string;
}

export function WeekPicker({
    range: rangeZoned,
    onChange,
    timezone,
}: WeekPickerProps): JSX.Element {
    const [visible, setVisible] = useState(false);

    function show() {
        return setVisible(true);
    }

    function hide() {
        return setVisible(false);
    }

    const applyModifier = useCallback(
        (modifier: (date: Date) => Date) =>
            onChange((range) => applyModifierToRange(range, modifier)),
        [onChange]
    );

    // Convert local browser time to sensor zoned time.
    const onChangeWithTimezone = useCallback(
        (newRange: Range) =>
            onChange(
                applyModifierToRange(newRange as WeekRange, (d) => zonedTimeToUtc(d, timezone))
            ),
        [onChange, timezone]
    );

    // Convert the sensor zoned time range back to local browser time.
    const range = useMemo(
        () => applyModifierToRange(rangeZoned, (d) => utcToZonedTime(d, timezone)),
        [rangeZoned, timezone]
    );

    const rangeText = useMemo(
        () =>
            [range.startDate, '-', range.endDate]
                .map((p) =>
                    p && isDate(p)
                        ? format(p as Date, { useBrowserTimezone: true, hideTime: true })
                        : p
                )
                .join(' '),
        [range]
    );

    const handleChange = useCallback(
        ({ selection }: RangeKeyDict) => {
            onChangeWithTimezone(getWeekRangeForDate(selection.startDate!));
        },
        [onChangeWithTimezone]
    );

    const handleCurrentWeekButton = useCallback(
        () => onChange(getThisWeekForTimezone(timezone)),
        [timezone, onChange]
    );

    return (
        <Tooltip
            visible={visible}
            onClickOutside={hide}
            interactive={true}
            content={
                <DateRange
                    weekStartsOn={1}
                    onChange={(item) => handleChange(item)}
                    ranges={[range]}
                    // Keeping the selection at range 0 step 0 prevents the range
                    // picker from entering the end date selection mode.
                    focusedRange={[0, 0]}
                    preventSnapRefocus={true}
                    rangeColors={['#F8DA28']}
                />
            }
        >
            <div className="text-dark-gray flex items-center gap-2 text-xs font-bold">
                <button
                    className="border-disabled-data-type-button cursor-pointer border px-1 py-0.5"
                    onClick={handleCurrentWeekButton}
                >
                    {gettext('THIS_WEEK')}
                </button>
                <div className="flex w-48 justify-evenly rounded border text-center">
                    <DateButton
                        icon={faChevronLeft}
                        onClick={() => applyModifier(addWeeksFp(-1))}
                    />
                    <button
                        className="border-disabled-data-type-button border-x px-1 py-0.5"
                        onClick={visible ? hide : show}
                    >
                        <span>{rangeText}</span>
                    </button>
                    <DateButton
                        icon={faChevronRight}
                        onClick={() => applyModifier(addWeeksFp(1))}
                    />
                </div>
            </div>
        </Tooltip>
    );
}
